import React from 'react';
import { useId } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../atoms/Buttons';
import { StyledLabel } from '../../../atoms/Label';
import { Modal } from '../../../atoms/Modal';
import { overCharacterLimitMessage } from '../../../atoms/Textarea';
import * as Styled from './AddCommentModal.styled';
import { useField } from '../../../hooks/useField';
import { ActivityFeedWhammyDispatchContext } from '../ActivityFeedContext';
import { FeedItemPreview } from './FeedItemPreview';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const isValidComment = comment => typeof comment === 'string' && comment.length > 0;

/**
 * Modal for leaving a comment or reply.
 *
 * * At the moment, the "add comment" and "add reply" modals are behaviorly identical and didn't
 * * warrant creating two copies of the modal. Let's revisit if behavior starts to diverge.
 */
export const AddCommentModal = ({
  feedItemId,
  renderTrigger,
  feedItemPreview,
  onReplySubmitted,
  loggedInMember,
  onLogin
}) => {
  const whammyDispatch = React.useContext(ActivityFeedWhammyDispatchContext);
  const {
    t
  } = useTranslation('blocks.activityFeed');
  if (!whammyDispatch) {
    throw new Error('whammyDispatch must be set');
  }
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [systemError, setSystemError] = React.useState('');
  const ariaLabelledById = useId();

  // Flag for adjusting text depending on whether the modal is for a comment or reply
  const isReplyModal = typeof feedItemId !== 'undefined' && typeof feedItemPreview !== 'undefined';

  // Comment/reply state
  const [commentIsOverCharacterLimit, setCommentIsOverCharacterLimit] = React.useState(false);
  const initialCommentValue = '';
  const {
    value,
    dirty,
    handleChange,
    handleBlur,
    errorMessage,
    setErrorMessage,
    isValid,
    resetField
  } = useField(initialCommentValue, isValidComment);

  // We want to know when the comment/reply is over the limit to set an appropriate error message
  const handleOnCharactersOverLimit = isOverLimit => {
    setCommentIsOverCharacterLimit(isOverLimit);
  };

  /**
   * Sets the error message on the textarea field.
   *
   * - If the value is greater than maxCharacters, show error.
   * - Clear the error when the value is valid or an empty string.
   *
   * Notes,
   * - An empty string clears the error because we only want to alert the user that the
   *   field is empty when they try to submit the form.
   * - Submitting the form also validates (and shows an error if necessary).
   */
  React.useEffect(() => {
    if (commentIsOverCharacterLimit) {
      const message = overCharacterLimitMessage(isReplyModal ? t('AddCommentModal.labelReply', 'Reply') : t('AddCommentModal.labelComment', 'Comment'), 500);
      setErrorMessage(message);
    } else if (isValid || value === '') {
      setErrorMessage('');
    }
  }, [value, isValid, commentIsOverCharacterLimit, setErrorMessage, isReplyModal, t]);
  const handleSetIsOpen = isOpenState => {
    if (!isOpenState) {
      // Clear the field, otherwise it will show up when the user re-opens the modal.
      resetField();
      setSystemError('');
    }
    if (loggedInMember?.id) {
      setIsOpen(isOpenState);
    } else if (onLogin) {
      onLogin();
    }
  };
  const handleSubmit = async ev => {
    ev.preventDefault();
    if (commentIsOverCharacterLimit) {
      return;
    }

    /**
     * Checking if the comment is empty is only done on submit to prevent the onblur scenario
     * where the user starts to enter a value, clears the text to start over, and gets an error
     * that the field is required. In that context, the field is not in an error state.
     */
    if (!isValidComment(value)) {
      const error = t(isReplyModal ? 'AddCommentModal.errorReplyValue' : 'AddCommentModal.errorCommentValue', 'Must have a value');
      setErrorMessage(error);
      return;
    }
    try {
      let payload = {
        type: 'ADD_USER_COMMENT',
        body: value,
        memberAvatarUrl: loggedInMember?.avatar || ''
      };
      if (isReplyModal) {
        payload = {
          type: 'ADD_REPLY',
          feedItemId,
          body: value,
          memberAvatarUrl: loggedInMember?.avatar || ''
        };
      }
      setIsSubmitting(true);
      await whammyDispatch(payload);
      if (isReplyModal && onReplySubmitted) {
        onReplySubmitted();
      }
      handleSetIsOpen(false);
    } catch (_err) {
      setSystemError(t(isReplyModal ? 'AddCommentModal.problemReplyMessage' : 'AddCommentModal.problemCommentMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };
  return /*#__PURE__*/_jsxs(Modal, {
    isOpen: isOpen,
    setIsOpen: handleSetIsOpen,
    closeButtonLabel: t('AddCommentModal.closeButtonLabel', 'Close'),
    ariaLabelledById: ariaLabelledById,
    renderTrigger: renderTrigger,
    children: [/*#__PURE__*/_jsx(Modal.Header, {
      children: /*#__PURE__*/_jsx(Modal.Title, {
        id: ariaLabelledById,
        children: t('AddCommentModal.modalTitle', 'Write words of support')
      })
    }), /*#__PURE__*/_jsx(Modal.Content, {
      children: /*#__PURE__*/_jsxs("div", {
        children: [feedItemPreview && /*#__PURE__*/_jsx(FeedItemPreview, {
          name: feedItemPreview.name,
          avatar: feedItemPreview.avatar,
          created: feedItemPreview.created,
          content: feedItemPreview.content
        }), /*#__PURE__*/_jsx(StyledLabel, {
          htmlFor: "comment",
          children: isReplyModal ? t('AddCommentModal.labelReply', 'Reply') : t('AddCommentModal.labelComment', 'Comment')
        }), /*#__PURE__*/_jsx(Styled.CommentTextarea, {
          id: "comment",
          maxCharacters: 500,
          errorContent: errorMessage,
          value: value,
          onChange: handleChange,
          onBlur: handleBlur,
          onCharactersOverLimit: handleOnCharactersOverLimit
        }), systemError !== '' && /*#__PURE__*/_jsx(Styled.SystemError, {
          children: systemError
        })]
      })
    }), /*#__PURE__*/_jsx(Modal.Footer, {
      children: /*#__PURE__*/_jsx(Modal.CtaButtonGroup, {
        children: /*#__PURE__*/_jsx(Button.PrimaryBrand, {
          "data-id": isReplyModal ? 'cp-activity-feed-confirm-add-reply' : 'cp-activity-feed-confirm-add-comment',
          type: "button",
          disabled: dirty && !isValid || errorMessage !== '' || commentIsOverCharacterLimit,
          onClick: handleSubmit,
          isLoading: isSubmitting,
          children: isReplyModal ? t('AddCommentModal.labelReply', 'Reply') : t('AddCommentModal.labelComment', 'Comment')
        })
      })
    })]
  });
};